/**
 * Market Helpers
 *
 * Pure functions that derive display values from a canonical
 * `IMarketEventDTO`. Replaces the previous widget-local view-model
 * (`MarketDetail`) by computing each derivation at the call site instead of
 * eagerly bundling them into a parallel object.
 *
 * Why pure helpers over a view-model:
 *  - Single source of truth for the API contract — `IMarketEventDTO` from
 *    `@repo/types`. Drift between widget and server is compiler-prevented.
 *  - Each helper is independently testable with no mocks.
 *  - Tree-shakeable per consumer.
 *
 * Naming convention: the canonical types use the `I*` prefix (`IMarketEventDTO`,
 * `IMarketDTO`); local helpers and value types are unprefixed. Variables and
 * parameters that hold a market event are named `marketEvent` (not `event`)
 * to make the entity unambiguous — disambiguates from DOM events, generic
 * domain events, etc.
 *
 * Future hoisting: where a moral equivalent already exists in `apps/web`, the
 * relevant JSDoc block points to it. When `apps/web` and the widget converge
 * on enough overlap, these helpers should move into `@repo/utils/markets` and
 * both sides import the shared module.
 */

import type { LibraryImage } from "@repo/types/image-library";
import type { IMarketDTO } from "@repo/types/market";
import type { IMarketEventDTO } from "@repo/types/prediction-event";
import { safeEventTicker } from "./url-sanitizer";

export const BinaryOutcome = {
  Yes: "yes",
  No: "no",
} as const;
export type BinaryOutcome = (typeof BinaryOutcome)[keyof typeof BinaryOutcome];

const trendingVolumeThreshold = 10_000;

/**
 * Numeric Yes price (0–1) for the primary market. Returns 0 if absent.
 *
 * Web-app equivalent: `apps/web/src/components/video-feed/.../use-video-feed-realtime-market.ts`
 * has a private one-liner of the same logic against `IMarketDTO`.
 */
export function getYesPrice(marketEvent: IMarketEventDTO): number {
  const raw = marketEvent.markets[0]?.currentPrice?.YES;
  return raw ? parseFloat(raw) : 0;
}

/** Numeric No price (0–1) for the primary market. Returns 0 if absent. */
export function getNoPrice(marketEvent: IMarketEventDTO): number {
  const raw = marketEvent.markets[0]?.currentPrice?.NO;
  return raw ? parseFloat(raw) : 0;
}

/**
 * Yes-side probability (0–100), rounded.
 *
 * Used by surfaces that always render the Yes side (e.g. the card price chart).
 * Distinct from {@link getDominantOutcome} which picks whichever side currently
 * leads — both helpers are needed.
 *
 * Web-app equivalent: `apps/web/src/utils/market-math.ts:currentPriceToPercents`
 * returns `{yes, no}` from a price slice.
 */
export function getYesProbability(marketEvent: IMarketEventDTO): number {
  return Math.round(getYesPrice(marketEvent) * 100);
}

/**
 * Whichever side currently has the higher price, plus its rounded probability.
 *
 * Tie-break rule: defaults to `"yes"` on exact equality (initial 50/50
 * markets). When both prices are 0 (uninitialized markets), returns
 * `{ outcome: "yes", probability: 0 }` by the same rule.
 *
 * Compares raw prices, not rounded percentages, so a 0.495 / 0.505 split
 * resolves to `"no"` rather than appearing tied at 50%.
 *
 * Web-app moral equivalent: `apps/web/src/components/video-feed/.../
 * video-feed-market-event.util.ts:getMarketOverlayData` runs the same logic
 * shaped for video-overlay output.
 */
export function getDominantOutcome(marketEvent: IMarketEventDTO): {
  outcome: BinaryOutcome;
  probability: number;
} {
  const yesPrice = getYesPrice(marketEvent);
  const noPrice = getNoPrice(marketEvent);
  return yesPrice >= noPrice
    ? { outcome: BinaryOutcome.Yes, probability: Math.round(yesPrice * 100) }
    : { outcome: BinaryOutcome.No, probability: Math.round(noPrice * 100) };
}

/** Multi-choice when the market event has more than one child market. */
export function isMultiChoice(marketEvent: IMarketEventDTO): boolean {
  return marketEvent.markets.length > 1;
}

/**
 * The fields {@link getMarketImageSrc} reads. Both `IMarketEventDTO` and
 * `IMarketDTO` satisfy it, so the same resolution serves event-level slots
 * (preview, card header) and per-market slots (contender rows).
 */
export interface MarketImageSource {
  libraryImage?: LibraryImage | null;
  imageUrl?: string | null;
}

/**
 * Image URL to render for a market or market event.
 *
 * Prefers the paired image-library asset (the Predictor-curated image, see
 * APPS-4912) over the raw provider `imageUrl` (e.g. Kalshi's), so every widget
 * surface shows the same paired image the web app does. Picks the `sm` variant
 * because every widget slot is a compact 58-64px thumbnail: no need to fetch
 * the default `md` src.
 *
 * Returns `undefined` when neither is set; callers hand that to `MarketImage`,
 * which renders the placeholder.
 *
 * Note: server-side enrichment currently pairs at the event level (by
 * eventTicker), so a child market's `libraryImage` is usually absent and this
 * resolves to its `imageUrl`. The same fallback keeps contender rows correct
 * once per-market pairing lands.
 */
export function getMarketImageSrc(
  source: MarketImageSource,
): string | undefined {
  return source.libraryImage?.sizes?.sm ?? source.imageUrl ?? undefined;
}

/**
 * Per-market trading volume. Used by the card outcome rows where each row
 * shows ITS market's volume — distinct from {@link getTotalVolume} which
 * sums across the event.
 */
export function getMarketVolume(market: IMarketDTO): number {
  return market.stats?.volumeTotal ?? 0;
}

/** Per-market 24h percentage change. */
export function getMarketPriceChange24h(market: IMarketDTO): number {
  return market.stats?.percentChange24h ?? 0;
}

/** Per-market YES probability (0–100), rounded. */
export function getMarketProbability(market: IMarketDTO): number {
  const raw = market.currentPrice?.YES;
  return raw ? Math.round(parseFloat(raw) * 100) : 0;
}

/**
 * Top N contenders for a multi-choice event, sorted by YES probability
 * descending. Defaults to 3 per the card spec ("a maximum of three outcomes
 * are displayed"). Ties on probability break by `volumeTotal` descending
 * (more-traded contender wins), then by `slug` ascending — guarantees a
 * deterministic order regardless of server response order.
 */
export function getTopContenders(
  marketEvent: IMarketEventDTO,
  max = 3,
): IMarketDTO[] {
  return [...marketEvent.markets]
    .sort((a, b) => {
      const probDiff = getMarketProbability(b) - getMarketProbability(a);
      if (probDiff !== 0) return probDiff;
      const volDiff = getMarketVolume(b) - getMarketVolume(a);
      if (volDiff !== 0) return volDiff;
      return a.slug.localeCompare(b.slug);
    })
    .slice(0, max);
}

/**
 * ISO close timestamp. Prefers `closesAt`, falls back to converting
 * `closeTime` (Unix seconds) to ISO. Returns `undefined` if neither is set.
 */
export function getExpiresAt(marketEvent: IMarketEventDTO): string | undefined {
  if (marketEvent.closesAt) return marketEvent.closesAt;
  if (marketEvent.closeTime !== undefined) {
    return new Date(marketEvent.closeTime * 1000).toISOString();
  }
  return undefined;
}

/**
 * Aggregate trading volume across all child markets.
 * Prefers event-level `stats.totalVolume`; falls back to summing per-market totals.
 */
export function getTotalVolume(marketEvent: IMarketEventDTO): number {
  if (marketEvent.stats?.totalVolume !== undefined) {
    return marketEvent.stats.totalVolume;
  }
  return marketEvent.markets.reduce(
    (sum, m) => sum + (m.stats?.volumeTotal ?? 0),
    0,
  );
}

/**
 * 24h percentage change.
 * Prefers event-level `priceChange24h`; falls back to the first market's stats.
 */
export function getPriceChange(marketEvent: IMarketEventDTO): number {
  return (
    marketEvent.priceChange24h ??
    marketEvent.markets[0]?.stats?.percentChange24h ??
    0
  );
}

/** Heuristic: market events above the volume threshold get the trending badge. */
export function getIsTrending(marketEvent: IMarketEventDTO): boolean {
  return getTotalVolume(marketEvent) > trendingVolumeThreshold;
}

/**
 * Web-app URL for the market event's detail page.
 *
 * `baseUrl` is the configured `exploreMarketsUrl`. Trailing slashes tolerated.
 *
 * Optional `options` deep-link a specific outcome on landing — the web app's
 * `market-event-page.tsx` reads these params to pre-select the side / contender
 * in the trading modal:
 *   • `side` — sets `selectedOption=yes|no` (binary card's YES/NO rows)
 *   • `market` — sets `market={slug}` (multi-choice contender selection)
 *     and is paired with `side=yes` when the widget should open trading
 *   • `partnerKey` — sets `pw_key={apiKey}`, the publisher API key the web app
 *     resolves server-side to the originating partner id
 */
export interface GetMarketUrlOptions {
  market?: IMarketDTO;
  side?: BinaryOutcome;
  partnerKey?: string;
}

export function getMarketUrl(
  marketEvent: IMarketEventDTO,
  baseUrl: string,
  options: GetMarketUrlOptions = {},
): string {
  const path = `${baseUrl.replace(/\/$/, "")}/${safeEventTicker(marketEvent.eventTicker)}`;
  const params = new URLSearchParams();
  if (options.market) params.set("market", options.market.slug);
  if (options.side) params.set("selectedOption", options.side);
  if (options.partnerKey) params.set("pw_key", options.partnerKey);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * First market's description, falling back to its title, then empty string.
 *
 * Uses `||` (not `??`) on purpose: empty-string descriptions should also fall
 * through to the title.
 */
export function getDescription(marketEvent: IMarketEventDTO): string {
  const primary = marketEvent.markets[0];
  if (!primary) return "";
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string descriptions should fall back to title
  return primary.description || primary.title || "";
}

/**
 * Predicate — does the market event have enough data for the widget to
 * display it?
 *
 * Used at the data boundary in `useMarketEventData` to gate the `"ready"` state.
 * The canonical `IMarketEventDTO` says these fields are required, but
 * defensive validation here protects against API drift / malformed payloads.
 */
export function isMarketEventRenderable(marketEvent: IMarketEventDTO): boolean {
  const primary = marketEvent.markets[0];
  if (!primary) return false;
  // Parsed-finite check (not just truthy) so malformed strings like "abc"
  // get rejected here instead of producing "NaN%" downstream.
  const yes = parseFloat(primary.currentPrice?.YES ?? "");
  const no = parseFloat(primary.currentPrice?.NO ?? "");
  if (!Number.isFinite(yes) || !Number.isFinite(no)) return false;
  if (!primary.slug) return false;
  if (!marketEvent.eventTicker) return false;
  // Reject events whose Kalshi-published close time has already passed.
  // Cache lag during the eviction window can briefly leave a just-closed
  // event in the response with stale prices — without this check the
  // widget would render it as if it were still tradable, and a click
  // would land on a closed market page. closeTime is Unix seconds (see
  // `apps/recommendation/.../embedding.service.ts` freshness filter).
  // Missing closeTime is treated as "unknown, allow" to match this
  // predicate's defensive philosophy (reject known-bad, allow unknown).
  if (
    marketEvent.closeTime !== undefined &&
    marketEvent.closeTime * 1000 <= Date.now()
  ) {
    return false;
  }
  return true;
}
